import Head from 'next/head';
import PropTypes from 'prop-types';
import Script from 'next/script';

const Metadata = ({ metadata }) => {
  if (!metadata) return;

  const vanillaScripts = metadata.headSectionScripts
    .map((s) => {
      if (!s || s === '') return null;

      return s
        .replace(/<script\s+[^>]*id=["']([^"']+)["'][^>]*>([\s\S]*?)<\/script>/gi, '')
        .replace(/<script[^>]*>/g, '')
        .replace(/<\/script>/g, '');
    })
    .join('\n');

  const scriptsWithIds = [];
  metadata.headSectionScripts.map((s) => {
    if (s && s !== '') {
      const match = /<script\s+[^>]*id=["']([^"']+)["'][^>]*>([\s\S]*?)<\/script>/gi.exec(s);

      if (match) {
        scriptsWithIds.push({ id: match[1], content: match[2] });
      }
    }
  });

  const scriptSrcs = [];
  metadata.headSectionScripts.forEach((s) => {
    if (s && s !== '') {
      const match = /src\s*=\s*"([^"]+)/g.exec(s);
      if (match) {
        scriptSrcs.push(...match.filter((res) => !res.startsWith('src=')));
      }
    }
  });

  const hreflangs = [];

  if (metadata.languages && metadata.languages.length > 0) {
    let currentHost = null;
    if (process.env.NODE_ENV === 'development') {
      currentHost =
        (process.env.DEV_FRONTEND_HOST !== '' && process.env.DEV_FRONTEND_HOST) || 'https://qa.ultimacollection.com/';
    } else {
      if (typeof window === 'undefined') {
        currentHost = process.env.RESOURCE_PATH.slice(0, -1);
      } else {
        currentHost = window?.location?.origin;
      }
    }

    metadata.languages.forEach((language) => {
      if (language.language === 'en-us') {
        hreflangs.push(<link rel="alternate" href={currentHost + language.languageUrl} hrefLang="x-default" />);
      }

      hreflangs.push(
        <link
          rel="alternate"
          href={currentHost + language.languageUrl}
          hrefLang={language.language === 'en-us' ? 'en' : language.language}
        />,
      );
    });
  }

  return (
    <>
      {scriptSrcs.map((script, index) => (
        <Script key={index} src={script} defer />
      ))}

      <Head>
        <title>{metadata.pageTitle}</title>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="keywords" content={metadata.metaKeywords} />
        <meta name="description" content={metadata.metaDescription} />
        <meta property="og:url" content={metadata.absolutePageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={metadata.openGraphPageTitle} />
        <meta property="og:description" content={metadata.openGraphDescription} />
        <meta property="og:image" content={metadata.openGraphImageUrl} />
        <meta name="google-site-verification" content="8mOEpvKMwa78pH5UjLeWbBYvPFGV9bujDKS9EuZ-0eY" />
        <link rel="canonical" href={metadata.canonicalLink} />

        {...hreflangs}

        {metadata.hideFromSearch && <meta name="robots" content="noindex" />}

        {metadata.headSectionScripts && metadata.headSectionScripts.length > 0 && (
          <>
            <script
              dangerouslySetInnerHTML={{
                __html: vanillaScripts,
              }}
            />
            {scriptsWithIds.length > 0 &&
              scriptsWithIds.map((script) => (
                <script key={script.id} id={script.id} dangerouslySetInnerHTML={{ __html: script.content }} />
              ))}
          </>
        )}

        {metadata.structuredDataTags.map((element, index) => {
          let structuredData = element;
          const match = structuredData.match(
            /<script[^>]*type=["']application\/ld\+json["'][^>]*>\s*([\s\S]*?)\s*<\/script>/im,
          );

          if (match) {
            structuredData = match[1];
          }

          return (
            <script
              key={index}
              type="application/ld+json"
              id="structuredDataTags"
              dangerouslySetInnerHTML={{ __html: structuredData }}
            />
          );
        })}

        {metadata.favicon?.imageUrl && <link rel="icon" type="image/png" href={metadata.favicon.imageUrl} />}
      </Head>
    </>
  );
};

Metadata.propTypes = {
  metadata: PropTypes.object.isRequired,
};

export default Metadata;
